import { useEffect, useRef, useState } from "react";
import type { FeatureCollection } from "geojson";
import maplibregl, { type GeoJSONSource, type Map as MapLibreMap } from "maplibre-gl";
import type { CellSummary, CityManifest, Venue } from "../domain/types.js";

interface MapViewProps {
  manifest: CityManifest;
  cellGeoJson: FeatureCollection;
  coverageCellGeoJson: FeatureCollection;
  venueGeoJson: FeatureCollection;
  cellSummaries: readonly CellSummary[];
  venues: readonly Venue[];
  showCoverage: boolean;
  userLocation?: [number, number];
  onSelectCell: (cell: CellSummary) => void;
  onSelectVenue: (venue: Venue) => void;
}

const categoryColor = [
  "match",
  ["get", "category"],
  "food",
  "#ff6f61",
  "groceries",
  "#65d6c0",
  "retail",
  "#f4c95d",
  "services",
  "#7fb3ff",
  "entertainment",
  "#ae81ff",
  "#d8d4e2",
] as any;

const MAP_LOAD_TIMEOUT_MS = 15_000;

export function MapView({
  manifest,
  cellGeoJson,
  coverageCellGeoJson,
  venueGeoJson,
  cellSummaries,
  venues,
  showCoverage,
  userLocation,
  onSelectCell,
  onSelectVenue,
}: MapViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const userMarkerRef = useRef<maplibregl.Marker | null>(null);
  const selectionRefs = useRef({ cellSummaries, venues, onSelectCell, onSelectVenue });
  const dataRefs = useRef({ cellGeoJson, coverageCellGeoJson, venueGeoJson });
  const coverageRef = useRef(showCoverage);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [mapStatus, setMapStatus] = useState<"loading" | "ready" | "failed">("loading");
  const [mapError, setMapError] = useState<string>();
  selectionRefs.current = { cellSummaries, venues, onSelectCell, onSelectVenue };
  dataRefs.current = { cellGeoJson, coverageCellGeoJson, venueGeoJson };
  coverageRef.current = showCoverage;

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    let map: MapLibreMap;
    let didLoad = false;
    let retryTimer: number | undefined;
    let latestError: string | undefined;

    setMapStatus("loading");
    setMapError(undefined);

    const recover = (message: string) => {
      if (retryTimer !== undefined) return;
      setMapError(message);
      if (loadAttempt === 0) {
        retryTimer = window.setTimeout(() => setLoadAttempt((attempt) => attempt + 1), 250);
      } else {
        setMapStatus("failed");
      }
    };

    try {
      map = new maplibregl.Map({
        container: containerRef.current,
        style: manifest.city.mapStyleUrl,
        center: manifest.city.center,
        zoom: manifest.city.defaultZoom,
        minZoom: 9,
        maxZoom: 18,
        attributionControl: false,
      });
    } catch (reason) {
      recover(reason instanceof Error ? reason.message : "The map could not start.");
      return () => {
        if (retryTimer !== undefined) window.clearTimeout(retryTimer);
      };
    }

    mapRef.current = map;
    map.on("error", (event) => {
      if (!didLoad) latestError = event.error?.message;
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "bottom-right");
    map.addControl(
      new maplibregl.AttributionControl({
        compact: true,
        customAttribution: "Venue data © OpenStreetMap contributors",
      }),
      "bottom-right",
    );

    const loadTimer = window.setTimeout(() => {
      if (!didLoad) {
        recover(latestError ?? "The map took too long to render.");
      }
    }, MAP_LOAD_TIMEOUT_MS);

    map.on("load", () => {
      map.addSource("cells", { type: "geojson", data: dataRefs.current.cellGeoJson });
      map.addSource("coverage-cells", {
        type: "geojson",
        data: dataRefs.current.coverageCellGeoJson,
      });
      map.addSource("venues", { type: "geojson", data: dataRefs.current.venueGeoJson });

      map.addLayer({
        id: "open-heat",
        type: "heatmap",
        source: "venues",
        maxzoom: 14,
        filter: ["==", ["get", "state"], "open"],
        layout: { visibility: coverageRef.current ? "none" : "visible" },
        paint: {
          "heatmap-weight": 1,
          "heatmap-intensity": ["interpolate", ["linear"], ["zoom"], 9, 0.6, 14, 1.7],
          "heatmap-radius": ["interpolate", ["linear"], ["zoom"], 9, 16, 14, 34],
          "heatmap-opacity": ["interpolate", ["linear"], ["zoom"], 9, 0.65, 14, 0.15],
          "heatmap-color": [
            "interpolate",
            ["linear"],
            ["heatmap-density"],
            0,
            "rgba(8,6,15,0)",
            0.2,
            "rgba(102,47,105,0.26)",
            0.5,
            "rgba(220,67,92,0.58)",
            0.78,
            "rgba(255,112,75,0.82)",
            1,
            "#ffe7a7",
          ],
        },
      });

      map.addLayer({
        id: "cells-fill",
        type: "fill",
        source: "cells",
        minzoom: 10,
        maxzoom: 15,
        layout: { visibility: coverageRef.current ? "none" : "visible" },
        paint: {
          "fill-color": [
            "case",
            [">", ["get", "open"], 0],
            [
              "interpolate",
              ["linear"],
              ["get", "intensity"],
              0,
              "#4b263f",
              0.5,
              "#bd405b",
              1,
              "#ff7c5c",
            ],
            "#191323",
          ],
          "fill-opacity": ["interpolate", ["linear"], ["zoom"], 10, 0.1, 13, 0.3, 15, 0.06],
        },
      });
      map.addLayer({
        id: "cells-outline",
        type: "line",
        source: "cells",
        minzoom: 11,
        maxzoom: 15,
        paint: {
          "line-color": [
            "case",
            ["<", ["get", "knownPercent"], 45],
            "#f4c95d",
            "rgba(255,255,255,0.22)",
          ],
          "line-width": ["interpolate", ["linear"], ["zoom"], 11, 0.3, 15, 1.2],
          "line-opacity": 0.7,
        },
      });
      map.addLayer(
        {
          id: "hours-coverage",
          type: "fill",
          source: "coverage-cells",
          minzoom: 9,
          layout: { visibility: coverageRef.current ? "visible" : "none" },
          paint: {
            "fill-color": [
              "interpolate",
              ["linear"],
              ["get", "hoursCoveragePercent"],
              0,
              "#d84a5f",
              50,
              "#f4c95d",
              100,
              "#65d6c0",
            ],
            "fill-opacity": ["interpolate", ["linear"], ["zoom"], 9, 0.45, 15, 0.62],
          },
        },
        "cells-outline",
      );
      map.addLayer({
        id: "venue-halo",
        type: "circle",
        source: "venues",
        minzoom: 13.2,
        filter: ["==", ["get", "state"], "open"],
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 13.2, 6, 17, 11],
          "circle-color": "#fff8e7",
          "circle-opacity": 0.92,
          "circle-blur": 0.65,
        },
      });
      map.addLayer({
        id: "venue-points",
        type: "circle",
        source: "venues",
        minzoom: 13.2,
        filter: ["==", ["get", "state"], "open"],
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 13.2, 3, 17, 6],
          "circle-color": categoryColor,
          "circle-stroke-width": 1,
          "circle-stroke-color": "#17121f",
          "circle-opacity": 1,
        },
      });

      const setPointer = () => (map.getCanvas().style.cursor = "pointer");
      const clearPointer = () => (map.getCanvas().style.cursor = "");
      map.on("mouseenter", "cells-fill", setPointer);
      map.on("mouseleave", "cells-fill", clearPointer);
      map.on("mouseenter", "hours-coverage", setPointer);
      map.on("mouseleave", "hours-coverage", clearPointer);
      map.on("mouseenter", "venue-points", setPointer);
      map.on("mouseleave", "venue-points", clearPointer);
      const selectCell = (event: maplibregl.MapLayerMouseEvent) => {
        const h3 = event.features?.[0]?.properties?.h3 as string | undefined;
        const cell = selectionRefs.current.cellSummaries.find((item) => item.h3 === h3);
        if (cell) selectionRefs.current.onSelectCell(cell);
      };
      map.on("click", "cells-fill", selectCell);
      map.on("click", "hours-coverage", selectCell);
      map.on("click", "venue-points", (event) => {
        event.originalEvent.stopPropagation();
        const id = event.features?.[0]?.properties?.id as string | undefined;
        const venue = selectionRefs.current.venues.find((item) => item.id === id);
        if (venue) selectionRefs.current.onSelectVenue(venue);
      });
      didLoad = true;
      if (loadTimer !== undefined) window.clearTimeout(loadTimer);
      setMapStatus("ready");
      setMapError(undefined);
    });

    return () => {
      if (loadTimer !== undefined) window.clearTimeout(loadTimer);
      if (retryTimer !== undefined) window.clearTimeout(retryTimer);
      userMarkerRef.current?.remove();
      userMarkerRef.current = null;
      map.remove();
      mapRef.current = null;
    };
  }, [loadAttempt, manifest]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const cellsSource = map.getSource("cells") as GeoJSONSource | undefined;
    const coverageCellsSource = map.getSource("coverage-cells") as GeoJSONSource | undefined;
    const venuesSource = map.getSource("venues") as GeoJSONSource | undefined;
    cellsSource?.setData(cellGeoJson);
    coverageCellsSource?.setData(coverageCellGeoJson);
    venuesSource?.setData(venueGeoJson);
  }, [cellGeoJson, coverageCellGeoJson, venueGeoJson]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map?.isStyleLoaded() || !map.getLayer("hours-coverage")) return;
    map.setLayoutProperty("hours-coverage", "visibility", showCoverage ? "visible" : "none");
    map.setLayoutProperty("open-heat", "visibility", showCoverage ? "none" : "visible");
    map.setLayoutProperty("cells-fill", "visibility", showCoverage ? "none" : "visible");
  }, [showCoverage]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !userLocation) return;

    if (!userMarkerRef.current) {
      const markerElement = document.createElement("div");
      markerElement.className = "user-location-marker";
      markerElement.setAttribute("aria-label", "Your location");
      const pulse = document.createElement("span");
      pulse.className = "user-location-pulse";
      const pin = document.createElement("span");
      pin.className = "user-location-pin";
      markerElement.append(pulse, pin);
      userMarkerRef.current = new maplibregl.Marker({
        element: markerElement,
        anchor: "bottom",
      }).addTo(map);
    }

    userMarkerRef.current.setLngLat(userLocation);
    map.easeTo({
      center: userLocation,
      zoom: Math.max(map.getZoom(), 14),
      duration: 1000,
    });
  }, [userLocation]);

  return (
    <>
      <div
        ref={containerRef}
        className="map"
        aria-label={`Interactive ${manifest.city.displayName} venue map`}
      />
      {mapStatus !== "ready" && (
        <div
          className={`map-status ${mapStatus}`}
          role={mapStatus === "failed" ? "alert" : "status"}
        >
          {mapStatus === "loading" ? (
            <span>Waking the map…</span>
          ) : (
            <>
              <strong>The map did not wake up.</strong>
              <span>{mapError ?? "Its renderer was unavailable."}</span>
              <button type="button" onClick={() => setLoadAttempt((attempt) => attempt + 1)}>
                Try map again
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
}
